import { QueryClient, QueryClientProvider, useSuspenseQuery } from '@tanstack/react-query';
import { initLocalJwtToken } from 'auth/auth-helpers/local-jwt';
import { LoadingPage } from 'components/molecules/loading-page/LoadingPage';
import { AppError } from 'modules/app/components/pages/app-error/AppError';
import { useConfig } from 'providers/config/ConfigProvider';
import { ReactElement, Suspense, useEffect } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Navigate } from 'react-router-dom';
import { AuthStore } from '../../auth/storage/auth-storage-manager';
import {
  getPostAuthCodeAndVerifier,
  getPostAuthNavigationPath,
  getPostAuthNavigationPathAfSso,
  getPostOAuthToken
} from '../auth-helpers/auth-helper';

import { useAuth } from 'auth/auth-provider/AuthProvider';
import { getCurrentAuthMethod } from 'auth/auth-methods/current-auth-method';
import { replaceWithDynamicHost } from 'utils/api/api-host-utils';

interface AuthPageProps {
  errorPage: ReactElement;
}

const OAuthRedirectPage = ({ errorPage }: AuthPageProps) => {
  const { auth, apiUrl, apiHostMappings } = useConfig();
  const { retrieveUserInfo, setToken } = useAuth();
  const postAuthNavigationPath = getPostAuthNavigationPath();
  const { code, codeVerifier } = getPostAuthCodeAndVerifier();

  // Override/map tokenProvider with API domain relevant to currently visited origin
  auth.afSso.tokenProvider = replaceWithDynamicHost(auth.afSso.tokenProvider, apiHostMappings);

  const { data: authToken, isError: authTokenError } = useSuspenseQuery({
    queryFn: () => getPostOAuthToken(code!, codeVerifier!, auth),
    queryKey: ['oauth-token', code],
    staleTime: 5000
  });

  const { isError: userInfoError } = useSuspenseQuery({
    queryFn: () => retrieveUserInfo(authToken?.access_token),
    queryKey: ['user-info', authToken?.access_token]
  });

  const env = useConfig();
  const { isUsingAfSso } = getCurrentAuthMethod(env);
  const jwtProvider = isUsingAfSso ? auth.afSso.jwtProvider : auth.client.jwtProvider;

  useSuspenseQuery({
    queryKey: ['local-jwt', authToken?.access_token],
    queryFn: () =>
      initLocalJwtToken({
        localJwtProvider: jwtProvider,
        gatewayToken: authToken!,
        bffApiUrl: replaceWithDynamicHost(apiUrl, apiHostMappings)
      })
  });

  useEffect(() => {
    AuthStore.setAuthToken(authToken);
    if (setToken) setToken(authToken);
  }, [authToken, setToken]);

  if (authTokenError || !authToken || userInfoError) {
    return errorPage;
  }

  return <Navigate to={isUsingAfSso ? getPostAuthNavigationPathAfSso() : postAuthNavigationPath} />;
};

export const ConfiguredRedirectUri = () => {
  const env = useConfig();

  const { isUsingClient, isUsingAfSso } = getCurrentAuthMethod(env);

  if (!isUsingClient && !isUsingAfSso) return <AppError />;

  const queryClient = new QueryClient();

  return (
    <ErrorBoundary FallbackComponent={AppError}>
      <QueryClientProvider client={queryClient}>
        <Suspense fallback={<LoadingPage />}>
          <OAuthRedirectPage errorPage={<AppError />} />
        </Suspense>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};
